import React, { useState, useRef, useEffect } from 'react';
import { FaUser, FaLock, FaMapPin, FaHeart, FaComment, FaShare, FaTimes, FaCamera, FaImage } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useCommunity } from '../contexts/CommunityContext';
import CommunityFeed from '../components/Community/CommunityFeed';
import mockUsers from '../data/mockUsers';
import '../styles/Community.css';

export default function Community() {
  const navigate = useNavigate();
  const { currentUser, isLoggedIn, login, logout } = useUser();
  const { addContent } = useCommunity();
  
  // State to track login status
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [showPostModal, setShowPostModal] = useState(false);
  const [newPost, setNewPost] = useState({
    content: '',
    location: '',
    images: []
  });
  const fileInputRef = useRef(null);
  
  // Auto-login on component mount if not already logged in
  // Using a ref to ensure this only runs once on initial mount
  const initialLoginAttempted = useRef(false);
  
  useEffect(() => {
    // Only attempt auto-login if we haven't tried before and there's no current user
    if (!initialLoginAttempted.current && !currentUser) {
      initialLoginAttempted.current = true;
      
      // Uncomment this block if you want auto-login
      // const defaultUser = mockUsers[0]; // Using the first user in the mockUsers array
      // 
      // login({
      //   id: defaultUser.id,
      //   username: defaultUser.username,
      //   name: defaultUser.name,
      //   avatar: defaultUser.avatar,
      //   isLocalQueer: defaultUser.isLocalQueer,
      //   bio: defaultUser.bio,
      //   location: defaultUser.location,
      // });
      // 
      // console.log('Auto-login successful with user:', defaultUser.username);
      // setIsUserLoggedIn(true);
    }
  }, [currentUser, login]);
  
  // We'll use the mockUsers data for authentication
  
  // Dummy posts data
  const [posts, setPosts] = useState([
    {
      id: 1,
      user: {
        name: 'Maria Johnson',
        avatar: 'https://randomuser.me/api/portraits/women/65.jpg'
      },
      location: 'Willemstad, Curaçao',
      date: '2 days ago',
      content: 'Just spent an amazing week exploring the colorful streets of Willemstad! The architecture is stunning and the people are so friendly.',
      images: [
        'https://cf.bstatic.com/xdata/images/hotel/max1024x768/356787622.jpg?k=1ad839e968b1ee3b2ed211f2bebb76fd3c9caa7f4d95764fbb5368c9196b9a21&o=&hp=1',
        'https://www.travelandleisure.com/thmb/VscfMXHNO6uZB7pZb5UxTzgYR2U=/1500x0/filters:no_upscale():max_bytes(150000):strip_icc()/willemstad-curacao-CURACAO0217-67a0fd400d4142e9a5d5f17551214306.jpg'
      ],
      likes: 42,
      comments: 8
    },
    {
      id: 2,
      user: {
        name: 'David Chen',
        avatar: 'https://randomuser.me/api/portraits/men/22.jpg'
      },
      location: 'Eagle Beach, Aruba',
      date: '1 week ago',
      content: 'Eagle Beach in Aruba is truly paradise! Crystal clear waters and the softest white sand I\'ve ever felt. Definitely coming back next year!',
      images: [
        'https://media-cdn.tripadvisor.com/media/photo-s/2c/09/6e/ee/cabanas-aerial.jpg',
        'https://www.planetware.com/wpimages/2020/01/aruba-in-pictures-beautiful-places-to-photograph-eagle-beach.jpg'
      ],
      likes: 87,
      comments: 15
    },
    {
      id: 3,
      user: {
        name: 'Sophie Williams',
        avatar: 'https://randomuser.me/api/portraits/women/33.jpg'
      },
      location: 'Kralendijk, Bonaire',
      date: '3 weeks ago',
      content: 'Bonaire has the most amazing diving spots! The coral reefs are vibrant and full of life. If you love marine life, this is the place to be.',
      images: [
        'https://www.sante.nl/wp-content/uploads/2023/07/bonaire-van-europa-2024.jpg',
        'https://www.tourismbonaire.com/includes/images/gallery/kralendijk-waterfront.jpg'
      ],
      likes: 65,
      comments: 12
    }
  ]);
  
  const handleLogin = (e) => {
    e.preventDefault();
    
    // Find user with matching username and password
    const user = mockUsers.find(
      user => user.username === username && user.password === password
    );
    
    if (user) {
      // Login successful
      login({
        id: user.id,
        username: user.username,
        name: user.name,
        avatar: user.avatar,
        isLocalQueer: user.isLocalQueer,
        bio: user.bio,
        location: user.location,
        // Add other user properties as needed
      });
      setLoginError('');
      console.log('Login successful:', user.username);
      // Clear form fields after successful login
      setUsername('');
      setPassword('');
      setIsUserLoggedIn(true);
    } else {
      setLoginError('Invalid username or password. Try using any username from the mockUsers data with password "password123"');
      console.log('Login failed for:', username);
    }
  };
  
  const handleLogout = () => {
    logout();
    setIsUserLoggedIn(false);
    console.log('User logged out');
  };
  
  const handleOpenPostModal = () => {
    setShowPostModal(true);
  };
  
  const handleClosePostModal = () => {
    setShowPostModal(false);
    setNewPost({
      content: '',
      location: '',
      images: []
    });
  };
  
  const handlePostChange = (e) => {
    const { name, value } = e.target;
    setNewPost(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      const newImages = files.map(file => URL.createObjectURL(file));
      setNewPost(prev => ({
        ...prev,
        images: [...prev.images, ...newImages].slice(0, 4) // Limit to 4 images
      }));
    }
  };
  
  const handleRemoveImage = (index) => {
    setNewPost(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };
  
  const handleSubmitPost = (e) => {
    e.preventDefault();
    
    // Create a new post with the form data
    const newPostData = {
      id: Date.now().toString(),
      contentType: 'post',
      author: {
        id: currentUser.id,
        name: currentUser.name,
        avatar: currentUser.avatar,
        isLocalQueer: currentUser.isLocalQueer
      },
      location: newPost.location,
      date: new Date().toISOString(),
      content: newPost.content,
      images: newPost.images.map((url, index) => ({
        url,
        alt: `Image ${index + 1} for post about ${newPost.location}`
      })),
      likes: [],
      comments: []
    };
    
    // Add the new post using the community context
    addContent(newPostData);
    
    // Close the modal and reset the form
    handleClosePostModal();
  };
  
  const renderLoginForm = () => {
    return (
      <div className="login-container">
        <div className="login-card">
          <h2>Login to Community</h2>
          <p className="login-subtitle">Share your Caribbean adventures with fellow travelers</p>
          
          {loginError && <div className="login-error">{loginError}</div>}
          
          <form onSubmit={handleLogin} className="login-form">
            <div className="form-group">
              <div className="input-icon">
                <FaUser />
              </div>
              <input 
                type="text" 
                placeholder="Username" 
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
              />
            </div>
            
            <div className="form-group">
              <div className="input-icon">
                <FaLock />
              </div>
              <input 
                type="password" 
                placeholder="Password" 
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            
            <button type="submit" className="login-button">Login</button>
          </form>
          
          <Link to="/user/account-creation" className="login-footer">
            <p>Don't have an account? <span>Sign up</span></p>
          </Link>
        </div>
      </div>
    );
  };
  
  const renderCommunityFeed = () => {
    // If currentUser is null, use default values
    const userAvatar = currentUser?.avatar || 'https://randomuser.me/api/portraits/lego/1.jpg';
    const userName = currentUser?.name || 'Guest User';
    
    return (
      <div className="community-container">
        <div className="community-header">
          <div className="user-welcome">
            <img src={userAvatar} alt={userName} className="user-avatar" />
            <div>
              <h3>Welcome, {userName}</h3>
              <button className="logout-button" onClick={handleLogout}>Logout</button>
            </div>
          </div>
          <div className="post-action">
            <button className="new-post-button" onClick={handleOpenPostModal}>Share Your Trip</button>
          </div>
        </div>
        
        <div className="posts-container">
          {posts.map(post => (
            <div className="post-card" key={post.id}>
              <div className="post-header">
                <img src={post.user.avatar} alt={post.user.name} className="post-avatar" />
                <div className="post-meta">
                  <h4>{post.user.name}</h4>
                  <div className="post-location">
                    <FaMapPin size={12} />
                    <span>{post.location}</span>
                    <span className="post-date">{post.date}</span>
                  </div>
                </div>
              </div>
              
              <p className="post-content">{post.content}</p>
              
              <div className="post-images">
                {post.images.map((image, index) => (
                  <div 
                    key={index} 
                    className="post-image" 
                    style={{backgroundImage: `url(${image})`, backgroundSize: 'cover'}}
                  ></div>
                ))}
              </div>
              
              <div className="post-actions">
                <div className="post-action-button">
                  <FaHeart /> <span>{post.likes}</span>
                </div>
                <div className="post-action-button">
                  <FaComment /> <span>{post.comments}</span>
                </div>
                <div className="post-action-button">
                  <FaShare />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Handle navigation to profiles page
  const handleViewProfiles = () => {
    navigate('/profiles');
  };

  return (
    <div className="community-page">
      <div className="community-hero">
        <div className="community-hero-overlay">
          <h1>OutCaribbean Community</h1>
          <p>Connect with fellow travelers and share your Caribbean experiences</p>
          
          {(isUserLoggedIn || currentUser) && (
            <div className="community-hero-actions">
              <button className="view-profiles-btn" onClick={handleViewProfiles}>
                View Community Profiles
              </button>
            </div>
          )}
        </div>
      </div>
      
      {isUserLoggedIn || currentUser ? (
        renderCommunityFeed()
      ) : (
        renderLoginForm()
      )}
      
      {/* Post Creation Modal */}
      {showPostModal && (
        <div className="modal-overlay">
          <div className="post-modal">
            <div className="modal-header">
              <h3>Share Your Trip</h3>
              <button className="close-modal" onClick={handleClosePostModal}>
                <FaTimes />
              </button>
            </div>
            
            <form onSubmit={handleSubmitPost} className="post-form">
              <div className="form-group">
                <label>Where did you go?</label>
                <div className="input-with-icon">
                  <FaMapPin className="form-icon" />
                  <input 
                    type="text" 
                    name="location" 
                    placeholder="Location (e.g. Willemstad, Curaçao)" 
                    value={newPost.location}
                    onChange={handlePostChange}
                    required
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Tell us about your experience</label>
                <textarea 
                  name="content" 
                  placeholder="Share your Caribbean adventure..." 
                  value={newPost.content}
                  onChange={handlePostChange}
                  required
                ></textarea>
              </div>
              
              <div className="form-group">
                <label>Add photos from your trip</label>
                <div className="image-upload-container">
                  <input 
                    type="file" 
                    accept="image/*" 
                    multiple 
                    ref={fileInputRef}
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                  <button 
                    type="button" 
                    className="upload-button" 
                    onClick={() => fileInputRef.current.click()}
                    disabled={newPost.images.length >= 4}
                  >
                    <FaCamera /> Select Images
                  </button>
                  <span className="upload-hint">{newPost.images.length}/4 images</span>
                </div>
                
                {newPost.images.length > 0 && (
                  <div className="image-preview-container">
                    {newPost.images.map((image, index) => (
                      <div className="image-preview" key={index}>
                        <img src={image} alt={`Preview ${index + 1}`} />
                        <button 
                          type="button" 
                          className="remove-image" 
                          onClick={() => handleRemoveImage(index)}
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="form-actions">
                <button type="button" className="cancel-button" onClick={handleClosePostModal}>Cancel</button>
                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={!newPost.content.trim() || !newPost.location.trim()}
                >
                  Post
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
